import React, { useState, useRef, useCallback } from 'react';

interface CurrencyInputProps {
  value: number;
  onChange: (value: number) => void;
  readOnly?: boolean;
  highlight?: 'calc' | 'total' | 'net';
  className?: string;
}

export function CurrencyInput({ value, onChange, readOnly, highlight, className = '' }: CurrencyInputProps) {
  // Local state so typing is instant — parent only updated on blur/enter
  const [localValue, setLocalValue] = useState<string>('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFocus = () => {
    setFocused(true);
    setLocalValue(value === 0 ? '' : String(value));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9.]/g, '');
    setLocalValue(raw);
  };

  const commit = useCallback(() => {
    setFocused(false);
    const parsed = parseFloat(localValue) || 0;
    onChange(parsed);
  }, [localValue, onChange]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === 'Tab') commit();
  };

  const displayValue = readOnly
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
    : focused
    ? localValue
    : value === 0
    ? ''
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  const bgClass = readOnly
    ? highlight === 'calc'
      ? 'bg-emerald-50 text-emerald-800 border-emerald-200 font-medium'
      : highlight === 'total'
      ? 'bg-secondary text-secondary-foreground border-border font-bold'
      : highlight === 'net'
      ? 'bg-primary text-primary-foreground border-primary font-bold text-base'
      : 'bg-muted text-muted-foreground border-border'
    : 'bg-background text-foreground border-input hover:border-ring/50 focus:border-ring focus:ring-2 focus:ring-ring/20 cursor-text';

  if (readOnly) {
    return (
      <div className={`font-mono text-right px-3 py-2 rounded-lg border text-sm w-full leading-tight ${bgClass} ${className}`}>
        {displayValue}
      </div>
    );
  }

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none select-none">$</span>
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={focused ? localValue : (value === 0 ? '' : new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value))}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        placeholder="0.00"
        className={`font-mono text-right px-3 py-2 pl-6 rounded-lg border text-sm w-full transition-all duration-150 outline-none ${bgClass} ${className}`}
      />
    </div>
  );
}

interface PercentInputProps {
  value: number;
  onChange: (value: number) => void;
}

export function PercentInput({ value, onChange }: PercentInputProps) {
  const [localValue, setLocalValue] = useState<string>('');
  const [focused, setFocused] = useState(false);

  const handleFocus = () => {
    setFocused(true);
    setLocalValue(value === 0 ? '' : String(value));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9.]/g, '');
    setLocalValue(raw);
  };

  const commit = useCallback(() => {
    setFocused(false);
    const parsed = parseFloat(localValue) || 0;
    onChange(parsed);
  }, [localValue, onChange]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === 'Tab') commit();
  };

  return (
    <div className="relative">
      <input
        type="text"
        inputMode="decimal"
        value={focused ? localValue : (value === 0 ? '' : String(value))}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        placeholder="0.00"
        className="font-mono text-right px-3 py-2 pr-7 rounded-lg border border-amber-400 bg-amber-50 text-amber-900 text-sm w-full transition-all duration-150 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
      />
      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-amber-600 text-sm pointer-events-none select-none font-medium">%</span>
    </div>
  );
}
